import { useEffect, useState } from "react"
import { Button, Card, Container, Spinner, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import { APIService } from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"

interface Recipient {
  id: string
  firstName: string
  lastName: string
  email: string
  isConfigured: boolean
  notifyOnNew: boolean
  notifyOnStatusChange: boolean
  notifyOnComment: boolean
  notifyOnDigest: boolean
}

interface Props {
  show: boolean
  toggle: (section: string) => void
}

const IncidentNotificationsSettings = ({ show, toggle }: Props) => {
  const { t } = useTranslation("global")
  const user: any = parsedUser()

  const [recipients, setRecipients] = useState<Recipient[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!show) return
    load()
  }, [show])

  const load = async () => {
    setIsLoading(true)
    try {
      const res = await APIService.getAction(
        "Incident/NotificationRecipients/List",
        `organizationId=${user.OrganizationId}`
      )
      if (res.data.status === "ok") setRecipients(res.data.response ?? [])
    } finally {
      setIsLoading(false)
    }
  }

  const toggle_field = (id: string, field: keyof Recipient) => {
    setRecipients(prev =>
      prev.map(r => r.id === id ? { ...r, [field]: !r[field] } : r)
    )
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const payload = {
        organizationId: user.OrganizationId,
        updatedId: user.ID,
        recipients: recipients
          .filter(r => r.notifyOnNew || r.notifyOnStatusChange || r.notifyOnComment || r.notifyOnDigest)
          .map(r => ({
            userId: r.id,
            notifyOnNew: r.notifyOnNew,
            notifyOnStatusChange: r.notifyOnStatusChange,
            notifyOnComment: r.notifyOnComment,
            notifyOnDigest: r.notifyOnDigest,
          })),
      }
      const res = await APIService.postAction("Incident/NotificationRecipients/Save", payload)
      if (res.data.status === "ok") {
        toast.success(t("record saved"))
        toggle("")
      }
    } finally {
      setIsSaving(false)
    }
  }

  if (!show) return null

  return (
    <Container fluid className="px-4 py-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 className="mb-0">
            <i className="fas fa-bell text-warning me-2" />
            {t("Notificaciones de incidencias")}
          </h5>
          <small className="text-muted">
            {t("Configura quién recibe alertas cuando se crean o actualizan incidencias. Si no hay nadie configurado, se notifica a todos los admins.")}
          </small>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={() => toggle("")}>
          <i className="fas fa-arrow-left me-1" />{t("Back")}
        </Button>
      </div>

      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          {isLoading ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : (
            <Table hover responsive className="mb-0 small">
              <thead className="table-light">
                <tr>
                  <th>{t("Usuario")}</th>
                  <th className="text-center">{t("Nuevas incidencias")}</th>
                  <th className="text-center">{t("Cambios de estado")}</th>
                  <th className="text-center">{t("Nuevos comentarios")}</th>
                  <th className="text-center">{t("Digest semanal (viernes)")}</th>
                </tr>
              </thead>
              <tbody>
                {recipients.map(r => (
                  <tr key={r.id} style={{ verticalAlign: "middle" }}>
                    <td>
                      <div className="fw-semibold">{r.firstName} {r.lastName}</div>
                      <div className="text-muted" style={{ fontSize: 11 }}>{r.email}</div>
                    </td>
                    <td className="text-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={r.notifyOnNew}
                        onChange={() => toggle_field(r.id, "notifyOnNew")}
                      />
                    </td>
                    <td className="text-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={r.notifyOnStatusChange}
                        onChange={() => toggle_field(r.id, "notifyOnStatusChange")}
                      />
                    </td>
                    <td className="text-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={r.notifyOnComment}
                        onChange={() => toggle_field(r.id, "notifyOnComment")}
                      />
                    </td>
                    <td className="text-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={r.notifyOnDigest}
                        onChange={() => toggle_field(r.id, "notifyOnDigest")}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {!isLoading && (
        <div className="mt-3 d-flex justify-content-end">
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving
              ? <><Spinner animation="border" size="sm" className="me-2" />{t("Guardando...")}</>
              : <><i className="fas fa-save me-2" />{t("Guardar configuración")}</>
            }
          </Button>
        </div>
      )}
    </Container>
  )
}

export default IncidentNotificationsSettings
